import type { OnboardingDataItemType } from "@memorang/constants/config/schema";
import { formatList } from "@memorang/helpers/i18n";
import type {
	ExamDetails,
	PracticeTestExam,
	StateDetails,
} from "@memorang/types";
import type { OnboardingProfile } from "./types";

export const PLAN_SECONDS_PER_ITEM = 90;

const DIAGNOSTIC_FALLBACK_ICON = "school-outline";

export type DiagnosticOption = {
	diagnostic: PracticeTestExam;
	title: string;
	/** Whether `title` was built from `topics` vs. falling back to the diagnostic's raw title. */
	hasTopics: boolean;
	topic: string | undefined;
};

const isAvailableDiagnostic = (diagnostic: PracticeTestExam) =>
	diagnostic.numItems > 0 && diagnostic.status !== "COMPLETE";

const matchesSkill = (diagnostic: PracticeTestExam, skill: string) =>
	diagnostic.title.toLocaleLowerCase().includes(skill.toLocaleLowerCase());

const diagnosticHasTopics = (diagnostic: PracticeTestExam) =>
	Boolean(diagnostic.topics?.length);

const getDiagnosticTitle = (diagnostic: PracticeTestExam, locale: string) =>
	diagnosticHasTopics(diagnostic)
		? formatList(locale, diagnostic.topics as string[])
		: diagnostic.title;

const getDiagnosticTopic = (
	diagnostic: PracticeTestExam,
	selectedSkillIds: string[],
) => {
	const topics = diagnostic.topics ?? [];
	const selectedTopic = topics.find((topic) =>
		selectedSkillIds.some(
			(skill) => skill.toLocaleLowerCase() === topic.toLocaleLowerCase(),
		),
	);
	return selectedTopic ?? topics[0];
};

export const getDiagnosticIcon = (
	topic: string | undefined,
	skillItems: OnboardingDataItemType[] | undefined,
) => {
	if (!topic) {
		return DIAGNOSTIC_FALLBACK_ICON;
	}
	const skillItem = skillItems?.find(
		(item) => item.type?.toLocaleLowerCase() === topic.toLocaleLowerCase(),
	);
	return skillItem?.icon ?? DIAGNOSTIC_FALLBACK_ICON;
};

export const getAvailableDiagnostics = (
	diagnostics: PracticeTestExam[],
	selectedSkillIds: string[],
	locale: string,
): DiagnosticOption[] => {
	const available = diagnostics.filter(isAvailableDiagnostic);

	let recommendedIndex = -1;
	for (const skill of selectedSkillIds) {
		recommendedIndex = available.findIndex((diagnostic) =>
			matchesSkill(diagnostic, skill),
		);
		if (recommendedIndex >= 0) {
			break;
		}
	}

	const ordered =
		recommendedIndex > 0
			? [
					available[recommendedIndex],
					...available.filter((_, index) => index !== recommendedIndex),
				]
			: available;

	return ordered.map((diagnostic) => ({
		diagnostic,
		title: getDiagnosticTitle(diagnostic, locale),
		hasTopics: diagnosticHasTopics(diagnostic),
		topic: getDiagnosticTopic(diagnostic, selectedSkillIds),
	}));
};

export const getRecommendedPlanId = ({
	daysUntilExam,
	totalItemCount,
	options,
}: {
	daysUntilExam: number;
	totalItemCount: number;
	options: { type?: string }[];
}): string | undefined => {
	if (daysUntilExam <= 0 || totalItemCount <= 0) {
		return undefined;
	}

	const planOptions: { id: string; minutes: number }[] = [];
	for (const option of options) {
		const minutes = Number(option.type);
		if (minutes != null && option.type != null) {
			planOptions.push({ id: option.type, minutes });
		}
	}
	if (planOptions.length === 0) {
		return undefined;
	}

	const dailyMinutes =
		(totalItemCount * PLAN_SECONDS_PER_ITEM) / 60 / daysUntilExam;

	let best = planOptions[0];
	let bestDiff = Math.abs(best.minutes - dailyMinutes);
	for (let i = 1; i < planOptions.length; i++) {
		const option = planOptions[i];
		const diff = Math.abs(option.minutes - dailyMinutes);
		if (diff < bestDiff) {
			best = option;
			bestDiff = diff;
		}
	}
	return best.id;
};

export const sanitizeProfile = (
	profile: Partial<OnboardingProfile>,
): OnboardingProfile => ({
	username: (profile.username?.trim() ?? "").replace(/[^a-zA-Z0-9]+/g, ""),
	firstName: profile.firstName?.trim() ?? "",
	lastName: profile.lastName?.trim() ?? "",
});

export function resolveI18n(
	field: Record<string, string>,
	locale: string,
): string;
export function resolveI18n(
	field: Record<string, string> | undefined,
	locale: string,
): string | undefined;
export function resolveI18n(
	field: Record<string, string> | undefined,
	locale: string,
) {
	if (!field) {
		return undefined;
	}
	return field[locale] ?? field["en-US"] ?? Object.values(field)[0];
}

export const groupExams = (exams: ExamDetails[]) => {
	const groups: Record<string, { id: string; name: string }[]> = {};
	for (const { exam } of exams) {
		const category = exam.category[0]?.name ?? "";
		if (!groups[category]) {
			groups[category] = [];
		}
		groups[category].push({ id: exam.id, name: exam.name });
	}
	return Object.entries(groups).map(([title, data]) => ({ title, data }));
};

const getRelatedExams = (
	exams: ExamDetails[],
	selectedExams: ExamDetails[],
): ExamDetails[] => {
	if (!selectedExams.length) {
		return [];
	}
	const relatedExamIds = selectedExams.flatMap(
		(exam) => exam.exam.relatedExams ?? [],
	);
	const relatedExams = exams.filter((exam) =>
		relatedExamIds.includes(exam.exam.id),
	);
	return [...selectedExams, ...relatedExams];
};

export const getStatesForCategory = (
	exams: ExamDetails[],
	categoryId: string,
): StateDetails[] => {
	const seen = new Set<string>();
	const states: StateDetails[] = [];
	for (const { exam } of exams) {
		const belongsToCategory = exam.category.some((c) => c.id === categoryId);
		if (belongsToCategory && exam.state && !seen.has(exam.state.code)) {
			seen.add(exam.state.code);
			states.push(exam.state);
		}
	}
	return states;
};

export const getFilteredExams = (
	exams: ExamDetails[],
	categoryId?: string,
	level?: "state" | "national" | "state-national",
	stateCode?: string,
): ExamDetails[] => {
	if (!categoryId) {
		return exams;
	}
	let filtered = exams.filter((exam) =>
		exam.exam.category.some((c) => c.id === categoryId),
	);

	switch (level) {
		case "national":
			filtered = filtered.filter((exam) => !exam.exam.state);
			break;
		case "state":
		case "state-national": {
			const selectedExams = filtered.filter(
				(exam) => exam.exam.state?.code === stateCode,
			);
			filtered = getRelatedExams(filtered, selectedExams);
			break;
		}
		default:
			return filtered;
	}

	return filtered;
};
